import { useEffect, useState } from 'react'
import { useQuery } from '@tanstack/react-query'
import { Card } from '../components/Card'
import { Icon } from '../components/Icon'
import { LearnTip, InfoIcon } from '../lib/learn-mode'
import { useCurrency } from '../lib/currency-context'
import { fmtMoney, FMT_INT } from '../lib/money'
import { Link } from 'react-router-dom'
import { LineChart } from '../components/charts/LineChart'
import { BarChart } from '../components/charts/BarChart'
import { TimeFilter } from '../components/TimeFilter'
import { type TimeRange, type Granularity, getRange } from '../lib/time-filter'
import { useTranslation } from 'react-i18next'

/**
 * My Dashboard — fully customizable, drag-drop, persistable.
 *
 * Layout is stored in localStorage as an array of widget refs.
 * Each widget is a small React component reading from the same API endpoints
 * the curated views (Pulse / Cash / Shipments / Customers) use.
 *
 * Drag & drop: HTML5 native (no extra dependency). Drag a widget by its
 * handle to reorder, drag from the catalog to add.
 */

// =========================================================================
// WIDGET REGISTRY — single source of truth for what's draggable
// =========================================================================
type Span = 3 | 4 | 6 | 8 | 12

interface WidgetSpec {
  id: string
  type: string
  span: Span
}

interface WidgetMeta {
  type: string
  title: string
  group: 'KPI' | 'Chart' | 'List'
  description: string
  defaultSpan: Span
  Component: React.FC
}

// ===== KPI widgets ========================================================
const KpiOperatingCash: React.FC = () => {
  const { t } = useTranslation()
  const { currency } = useCurrency()
  const { data } = useQuery<{ operating_cash_cents: number; delta_pct: number | null }>({
    queryKey: ['cash', 'operating', 'this_month'],
    queryFn: () => fetch('/api/cash/operating?period=this_month').then(r => r.json()),
  })
  return (
    <WidgetShell title={t('my_dashboard.operating_cash_mtd')} tip={t('my_dashboard.operating_cash_tip')}>
      <div className="text-[26px] font-bold">{data ? fmtMoney(data.operating_cash_cents, currency, { compact: true }) : '—'}</div>
      {data?.delta_pct != null && (
        <div className={`text-[11px] mt-1 font-medium ${data.delta_pct >= 0 ? 'text-mint-600' : 'text-flame-600'}`}>
          {data.delta_pct >= 0 ? '▲' : '▼'} {Math.abs(data.delta_pct)}% {t('common.vs_prior_month')}
        </div>
      )}
    </WidgetShell>
  )
}

const KpiActiveLabels: React.FC = () => {
  const { t } = useTranslation()
  const { data } = useQuery<{ ships: number; velocity_per_day: number }>({
    queryKey: ['ships', 'kpis', 'today'],
    queryFn: () => fetch('/api/shipments/kpis?period=today').then(r => r.json()),
  })
  return (
    <WidgetShell title={t('my_dashboard.active_labels_today')} tip={t('my_dashboard.active_labels_tip')}>
      <div className="text-[26px] font-bold">{data ? FMT_INT.format(data.ships) : '—'}</div>
      <div className="text-[11px] mt-1 text-ink-500">{data?.velocity_per_day ? `~${FMT_INT.format(data.velocity_per_day)}/day pace` : '—'}</div>
    </WidgetShell>
  )
}

const KpiMthlyProfit: React.FC = () => {
  const { t } = useTranslation()
  const { currency } = useCurrency()
  const { data } = useQuery<{ profit_cents: number; ships: number }>({
    queryKey: ['ships', 'kpis', 'this_month'],
    queryFn: () => fetch('/api/shipments/kpis?period=this_month').then(r => r.json()),
  })
  return (
    <WidgetShell title={t('my_dashboard.profit_mtd')} tip={t('my_dashboard.profit_mtd_tip')}>
      <div className="text-[26px] font-bold">{data ? fmtMoney(data.profit_cents, currency, { compact: true }) : '—'}</div>
      <div className="text-[11px] mt-1 text-ink-500">{data ? `${FMT_INT.format(data.ships)} ships` : '—'}</div>
    </WidgetShell>
  )
}

const KpiDepositFloat: React.FC = () => {
  const { t } = useTranslation()
  const { currency } = useCurrency()
  const { data } = useQuery<{ deposit_float_cents: number; active_customers: number }>({
    queryKey: ['customers', 'kpis'],
    queryFn: () => fetch('/api/customers/kpis').then(r => r.json()),
  })
  return (
    <WidgetShell title={t('my_dashboard.customer_deposit_float')} tip={t('my_dashboard.customer_deposit_float_tip')}>
      <div className="text-[26px] font-bold">{data?.deposit_float_cents != null ? fmtMoney(data.deposit_float_cents, currency, { compact: true }) : '—'}</div>
      <div className="text-[11px] mt-1 text-ink-500">{data ? `${FMT_INT.format(data.active_customers)} active customers` : '—'}</div>
    </WidgetShell>
  )
}

const KpiArpu: React.FC = () => {
  const { t } = useTranslation()
  const { currency } = useCurrency()
  const { data } = useQuery<{ arpu_30d_cents: number }>({
    queryKey: ['customers', 'kpis'],
    queryFn: () => fetch('/api/customers/kpis').then(r => r.json()),
  })
  return (
    <WidgetShell title={t('my_dashboard.arpu_30d')} tip={t('my_dashboard.arpu_30d_tip')}>
      <div className="text-[26px] font-bold">{data ? fmtMoney(data.arpu_30d_cents, currency) : '—'}</div>
      <div className="text-[11px] mt-1 text-ink-500">per active customer</div>
    </WidgetShell>
  )
}

const KpiLtv: React.FC = () => {
  const { t } = useTranslation()
  const { currency } = useCurrency()
  const { data } = useQuery<{ total_ltv_cents: number; total_customers_lifetime: number }>({
    queryKey: ['customers', 'kpis'],
    queryFn: () => fetch('/api/customers/kpis').then(r => r.json()),
  })
  return (
    <WidgetShell title={t('my_dashboard.total_ltv')} tip={t('my_dashboard.total_ltv_tip')}>
      <div className="text-[26px] font-bold">{data ? fmtMoney(data.total_ltv_cents, currency, { compact: true }) : '—'}</div>
      <div className="text-[11px] mt-1 text-ink-500">{data ? `${FMT_INT.format(data.total_customers_lifetime)} customers since inception` : '—'}</div>
    </WidgetShell>
  )
}

const KpiCarrierSpend: React.FC = () => {
  const { t } = useTranslation()
  const { currency } = useCurrency()
  const { data } = useQuery<{ carrier_30d_cents: number; carrier_30d_split: { usps_pct: number; dhl_pct: number } | null }>({
    queryKey: ['cash', 'kpis'],
    queryFn: () => fetch('/api/cash/kpis').then(r => r.json()),
  })
  return (
    <WidgetShell title={t('my_dashboard.carrier_spend_30d')} tip={t('my_dashboard.carrier_spend_30d_tip')}>
      <div className="text-[26px] font-bold">{data ? fmtMoney(data.carrier_30d_cents, currency, { compact: true }) : '—'}</div>
      <div className="text-[11px] mt-1 text-ink-500">
        {data?.carrier_30d_split ? `USPS ${data.carrier_30d_split.usps_pct}% · DHL ${data.carrier_30d_split.dhl_pct}%` : '—'}
      </div>
    </WidgetShell>
  )
}

const KpiLatestBalance: React.FC = () => {
  const { t } = useTranslation()
  const { currency } = useCurrency()
  const { data } = useQuery<{ latest_balance_cents: number; latest_balance_date: string | null }>({
    queryKey: ['cash', 'kpis'],
    queryFn: () => fetch('/api/cash/kpis').then(r => r.json()),
  })
  return (
    <WidgetShell title={t('my_dashboard.chase_balance')} tip={t('my_dashboard.chase_balance_tip')}>
      <div className="text-[26px] font-bold">{data?.latest_balance_cents != null ? fmtMoney(data.latest_balance_cents, currency, { compact: true }) : '—'}</div>
      <div className="text-[11px] mt-1 text-ink-500">As of {data?.latest_balance_date ?? '—'}</div>
    </WidgetShell>
  )
}

// ===== List widgets =======================================================
const ListAtRisk: React.FC = () => {
  const { t } = useTranslation()
  const { currency } = useCurrency()
  const { data } = useQuery<{ rows: Array<{ customer_name: string; days_silent: number; profit_90d_cents: number }> }>({
    queryKey: ['customers', 'at-risk'],
    queryFn: () => fetch('/api/customers/at-risk').then(r => r.json()),
  })
  return (
    <WidgetShell title={t('my_dashboard.at_risk_customers')} tip={t('my_dashboard.at_risk_tip')} link="/customers">
      {!data && <div className="text-[12px] text-ink-400">{t('common.loading')}</div>}
      {data?.rows.length === 0 && <div className="text-[12px] text-ink-400">No at-risk customers 🎉</div>}
      <div className="space-y-1.5 mt-1">
        {data?.rows.slice(0, 6).map(r => (
          <div key={r.customer_name} className="flex items-center justify-between text-[12px] py-1">
            <span className="font-medium truncate mr-2">{r.customer_name}</span>
            <span className="flex items-center gap-2">
              <span className="text-ink-500">{fmtMoney(r.profit_90d_cents, currency, { compact: true })}</span>
              <span className={`pill ${r.days_silent > 30 ? 'pill-flame' : 'pill-honey'} text-[10px]`}>{r.days_silent}d</span>
            </span>
          </div>
        ))}
      </div>
    </WidgetShell>
  )
}

const ListTopCustomers: React.FC = () => {
  const { t } = useTranslation()
  const { currency } = useCurrency()
  const { data } = useQuery<{ rows: Array<{ customer_name: string; ships: number; profit_cents: number; cumulative_pct: number }> }>({
    queryKey: ['ships', 'pareto', 'this_month'],
    queryFn: () => fetch('/api/shipments/pareto?period=this_month').then(r => r.json()),
  })
  return (
    <WidgetShell title={t('my_dashboard.top_customers_this_month')} tip={t('my_dashboard.top_customers_tip')} link="/shipments">
      <div className="space-y-1.5 mt-1">
        {data?.rows.slice(0, 6).map(r => (
          <div key={r.customer_name} className="flex items-center justify-between text-[12px] py-1">
            <span className="font-medium truncate mr-2">{r.customer_name}</span>
            <span className="font-semibold text-ink-700 num">{fmtMoney(r.profit_cents, currency, { compact: true })}</span>
          </div>
        ))}
      </div>
    </WidgetShell>
  )
}

const ListAffiliates: React.FC = () => {
  const { t } = useTranslation()
  const { currency } = useCurrency()
  const { data } = useQuery<{ payments: Array<{ id: number; partner_name: string; payable_amount_cents: number; status: string }> }>({
    queryKey: ['affiliate-payments-pending'],
    queryFn: () => fetch('/api/affiliates/payments').then(r => r.json()).catch(() => ({ payments: [] })),
  })
  const pending = (data?.payments ?? []).filter(p => p.status === 'pending' || p.status === 'approved')
  const total = pending.reduce((s, p) => s + p.payable_amount_cents, 0)
  return (
    <WidgetShell title={t('my_dashboard.affiliates_pending')} tip={t('my_dashboard.affiliates_pending_tip')} link="/affiliates">
      <div className="text-[20px] font-bold">{fmtMoney(total, currency, { compact: true })}</div>
      <div className="text-[11px] text-ink-500">{pending.length} payments</div>
      <div className="space-y-0.5 mt-2">
        {pending.slice(0, 4).map(p => (
          <div key={p.id} className="flex justify-between text-[11px]">
            <span className="truncate mr-2">{p.partner_name}</span>
            <span className="num font-medium">{fmtMoney(p.payable_amount_cents, currency)}</span>
          </div>
        ))}
      </div>
    </WidgetShell>
  )
}

const ListDisputesOpen: React.FC = () => {
  const { t } = useTranslation()
  const { currency } = useCurrency()
  const { data } = useQuery<{ kpis: { open: { n: number; total_minor: number } }; byCarrier: Array<{ carrier: string; n: number; total_minor: number }> }>({
    queryKey: ['disputes-summary'],
    queryFn: () => fetch('/api/disputes/summary').then(r => r.json()),
  })
  return (
    <WidgetShell title={t('my_dashboard.open_disputes')} tip={t('my_dashboard.open_disputes_tip')} link="/recovery?tab=disputes">
      <div className="text-[20px] font-bold">{data ? FMT_INT.format(data.kpis.open.n) : '—'}</div>
      <div className="text-[11px] text-ink-500 mb-2">
        {data ? `${fmtMoney(data.kpis.open.total_minor, currency, { compact: true })} at stake` : '—'}
      </div>
      <div className="space-y-0.5">
        {data?.byCarrier.slice(0, 4).map(c => (
          <div key={c.carrier} className="flex justify-between text-[11px]">
            <span>{c.carrier.toUpperCase()}</span>
            <span className="num font-medium">{fmtMoney(c.total_minor, currency, { compact: true })}</span>
          </div>
        ))}
      </div>
    </WidgetShell>
  )
}

// ===== CFO chart widgets ==================================================

// Generic per-chart hook — each widget passes its own endpoint + date range.
// React Query caches by [endpoint, from, to] so widgets with the same filter
// share a single in-flight request; different filters are independent.
function useChartData<T>(endpoint: string, from: string, to: string, granularity?: Granularity) {
  return useQuery<T>({
    queryKey: ['chart', endpoint, from, to, granularity],
    queryFn: async () => {
      const p = new URLSearchParams({ from, to })
      if (granularity) p.set('granularity', granularity)
      const r = await fetch(`/api/dashboard/${endpoint}?${p}`)
      if (!r.ok) throw new Error(`HTTP ${r.status}`)
      return r.json() as Promise<T>
    },
    staleTime: 5 * 60_000,
  })
}

// Compact USD formatter for chart axis labels ($12k, $1.2M, $850)
const fmtK = (v: number): string => {
  if (Math.abs(v) >= 1_000_000) return `$${(v / 1_000_000).toFixed(1)}M`
  if (Math.abs(v) >= 1_000)    return `$${(v / 1_000).toFixed(0)}k`
  return `$${v.toFixed(0)}`
}

// ── Chart style switcher ──────────────────────────────────────────────────
type ChartStyle = 'line' | 'bar' | 'bar_h'

// Options for time-series charts (line or vertical bar)
const TIME_STYLES: Array<{ value: ChartStyle; label: string }> = [
  { value: 'line',  label: 'Line' },
  { value: 'bar',   label: 'Bar'  },
]
// Options for categorical bar charts (horizontal vs vertical)
const CAT_STYLES: Array<{ value: ChartStyle; label: string }> = [
  { value: 'bar_h', label: 'Horiz.' },
  { value: 'bar',   label: 'Vert.'  },
]

function ChartStylePicker({
  value,
  options,
  onChange,
}: {
  value: ChartStyle
  options: Array<{ value: ChartStyle; label: string }>
  onChange: (s: ChartStyle) => void
}) {
  return (
    <div className="flex items-center gap-0.5 ml-auto">
      {options.map(o => (
        <button
          key={o.value}
          onClick={() => onChange(o.value)}
          className={[
            'text-[9px] px-1.5 py-0.5 rounded border transition-colors whitespace-nowrap leading-none',
            value === o.value
              ? 'bg-royal-600 text-white border-royal-600'
              : 'text-ink-400 border-ink-200 hover:border-royal-500 hover:text-royal-600',
          ].join(' ')}
        >
          {o.label}
        </button>
      ))}
    </div>
  )
}

const ChartLabelVolume: React.FC = () => {
  const [range, setRange]   = useState<TimeRange>(() => getRange('this_month'))
  const [style, setStyle]   = useState<ChartStyle>('bar')
  const { t } = useTranslation()
  const { data, isLoading, isError } = useChartData<{
    labelVolume: Array<{ period: string; ships: number }>
  }>('label-volume', range.from, range.to, range.granularity)
  const rows = data?.labelVolume ?? []
  return (
    <>
      <div className="text-[10px] font-semibold text-ink-500 uppercase tracking-wider mb-1 flex items-center gap-2">
        Label volume
        <InfoIcon tip="Shipments created over time. System accounts only." />
        <ChartStylePicker value={style} options={TIME_STYLES} onChange={setStyle} />
      </div>
      <TimeFilter value={range} onChange={setRange} />
      {isLoading  ? <div className="text-[12px] text-ink-400 py-2">{t('common.loading')}</div>
      : isError   ? <div className="text-[12px] text-flame-500 py-2">Failed to load</div>
      : !rows.length ? <div className="text-[12px] text-ink-400 py-2">No shipments in this period.</div>
      : style === 'line'
        ? <LineChart
            labels={rows.map(r => r.period)}
            height={200}
            series={[{ label: 'Labels', data: rows.map(r => r.ships), color: '#378ADD', fill: true }]}
          />
        : <BarChart
            labels={rows.map(r => r.period)}
            data={rows.map(r => r.ships)}
            color="#378ADD"
            height={200}
            barThickness={Math.max(4, Math.min(28, Math.floor(600 / Math.max(rows.length, 1))))}
          />}
    </>
  )
}

// ── Label volume by customer ──────────────────────────────────────────────
// Single-customer view: pick a customer + time period, see their shipment volume.
const ChartLabelVolumeByCustomer: React.FC = () => {
  const [range, setRange]       = useState<TimeRange>(() => getRange('this_month'))
  const [style, setStyle]       = useState<ChartStyle>('bar')
  const [customer, setCustomer] = useState<string>('')
  const { t } = useTranslation()

  // Customer list
  const { data: custData } = useQuery<{ customers: string[] }>({
    queryKey: ['dashboard-customers'],
    queryFn: () => fetch('/api/dashboard/customers').then(r => r.json()),
    staleTime: 60_000,
  })
  const customers = custData?.customers ?? []

  // Volume for selected customer
  const { data, isLoading, isError } = useQuery<{
    labelVolume: Array<{ period: string; ships: number }>
  }>({
    queryKey: ['chart', 'label-volume-by-customer', range.from, range.to, range.granularity, customer],
    queryFn: async () => {
      const p = new URLSearchParams({ from: range.from, to: range.to, customer, granularity: range.granularity })
      return fetch(`/api/dashboard/label-volume-by-customer?${p}`).then(r => r.json())
    },
    enabled: !!customer,
  })
  const rows = data?.labelVolume ?? []
  const total = rows.reduce((s, r) => s + r.ships, 0)

  return (
    <>
      <div className="text-[10px] font-semibold text-ink-500 uppercase tracking-wider mb-1 flex items-center gap-2">
        Label volume — customer
        <InfoIcon tip="Shipments over time for a single customer. System accounts only." />
        <ChartStylePicker value={style} options={TIME_STYLES} onChange={setStyle} />
      </div>
      <div className="flex items-center gap-2 mb-2">
        <select
          value={customer}
          onChange={e => setCustomer(e.target.value)}
          className="h-8 px-2 border border-ink-200 rounded-lg text-sm focus-ring bg-white min-w-[180px]"
        >
          <option value="">— select customer —</option>
          {customers.map(c => <option key={c} value={c}>{c}</option>)}
        </select>
      </div>
      <TimeFilter value={range} onChange={setRange} />
      {!customer
        ? <div className="text-[12px] text-ink-400 py-2">Select a customer above to view their shipment volume.</div>
        : isLoading ? <div className="text-[12px] text-ink-400 py-2">{t('common.loading')}</div>
        : isError   ? <div className="text-[12px] text-flame-500 py-2">Failed to load</div>
        : !rows.length ? <div className="text-[12px] text-ink-400 py-2">No shipments for this customer in the selected period.</div>
        : <>
            <div className="text-[11px] text-ink-400 mb-1">{total.toLocaleString()} total shipments</div>
            {style === 'line'
              ? <LineChart
                  labels={rows.map(r => r.period)}
                  height={200}
                  series={[{ label: 'Labels', data: rows.map(r => r.ships), color: '#378ADD', fill: true }]}
                />
              : <BarChart
                  labels={rows.map(r => r.period)}
                  data={rows.map(r => r.ships)}
                  color="#378ADD"
                  height={200}
                  barThickness={Math.max(4, Math.min(28, Math.floor(600 / Math.max(rows.length, 1))))}
                />}
          </>}
    </>
  )
}

// ── Real P&L chart ────────────────────────────────────────────────────────
// Revenue (label + subscriptions) vs all cost layers, with real profit on right axis.
// Stripe layers show as $0 until import_stripe.py has been run.
type RealPnlRow = {
  period:              string
  label_rev_minor:     number
  sub_rev_minor:       number
  total_rev_minor:     number
  carrier_cost_minor:  number
  stripe_fee_minor:    number
  affiliate_minor:     number
  personnel_minor:     number
  infra_minor:         number
  other_ops_minor:     number
  bank_fee_minor:      number
  total_cost_minor:    number
  real_profit_minor:   number
  ships:               number
}

const ChartRealPnl: React.FC = () => {
  const [range, setRange] = useState<TimeRange>(() => getRange('this_year'))
  const [style, setStyle] = useState<ChartStyle>('line')
  const { t } = useTranslation()
  const { data, isLoading, isError } = useChartData<{ realPnl: RealPnlRow[] }>(
    'real-pnl', range.from, range.to, range.granularity,
  )
  const rows = data?.realPnl ?? []
  const labels = rows.map(r => r.period)

  // Totals for the summary strip
  const totRev    = rows.reduce((s, r) => s + r.total_rev_minor,    0)
  const totCost   = rows.reduce((s, r) => s + r.total_cost_minor,   0)
  const totProfit = rows.reduce((s, r) => s + r.real_profit_minor,  0)
  const hasSubs   = rows.some(r => r.sub_rev_minor   > 0)
  const hasStripe = rows.some(r => r.stripe_fee_minor > 0)

  return (
    <>
      <div className="text-[10px] font-semibold text-ink-500 uppercase tracking-wider mb-1 flex items-center gap-2">
        Real P&amp;L — all costs
        <InfoIcon tip="Revenue (label sales + subscriptions) minus every cost layer: carrier, Stripe fees, affiliate commissions, ops spend, bank fees. Stripe rows are $0 until import_stripe.py runs." />
        <ChartStylePicker value={style} options={TIME_STYLES} onChange={setStyle} />
      </div>
      <TimeFilter value={range} onChange={setRange} />

      {/* Summary strip */}
      {rows.length > 0 && (
        <div className="grid grid-cols-3 gap-2 mb-3">
          <div className="bg-mint-50 rounded p-2">
            <div className="text-[9px] uppercase text-ink-500 font-semibold">Revenue</div>
            <div className="text-[14px] font-bold text-mint-700">{fmtK(totRev / 100)}</div>
            {hasSubs && <div className="text-[9px] text-ink-400">incl. subscriptions</div>}
          </div>
          <div className="bg-flame-50 rounded p-2">
            <div className="text-[9px] uppercase text-ink-500 font-semibold">Total costs</div>
            <div className="text-[14px] font-bold text-flame-700">{fmtK(totCost / 100)}</div>
            {!hasStripe && <div className="text-[9px] text-amber-600">⚠ Stripe fees missing — run import_stripe.py</div>}
          </div>
          <div className={`rounded p-2 ${totProfit >= 0 ? 'bg-royal-50' : 'bg-red-50'}`}>
            <div className="text-[9px] uppercase text-ink-500 font-semibold">Real profit</div>
            <div className={`text-[14px] font-bold ${totProfit >= 0 ? 'text-royal-700' : 'text-red-700'}`}>
              {fmtK(totProfit / 100)}
            </div>
            <div className="text-[9px] text-ink-400">
              {totRev > 0 ? `${((totProfit / totRev) * 100).toFixed(1)}% margin` : ''}
            </div>
          </div>
        </div>
      )}

      {isLoading  ? <div className="text-[12px] text-ink-400 py-2">{t('common.loading')}</div>
      : isError   ? <div className="text-[12px] text-flame-500 py-2">Failed to load</div>
      : !rows.length ? <div className="text-[12px] text-ink-400 py-2">{t('common.no_data_period')}</div>
      : style === 'line'
        ? <LineChart
            labels={labels}
            height={220}
            yTickFormat={fmtK}
            y2TickFormat={fmtK}
            series={[
              { label: 'Revenue',        data: rows.map(r => r.total_rev_minor   / 100), color: '#1D9E75', yAxis: 'left' },
              { label: 'Total costs',    data: rows.map(r => r.total_cost_minor  / 100), color: '#D85A30', yAxis: 'left', dashed: true },
              { label: 'Real profit',    data: rows.map(r => r.real_profit_minor / 100), color: '#378ADD', yAxis: 'right', fill: true },
            ]}
          />
        : <BarChart
            labels={labels}
            series={[
              { label: 'Carrier cost',   data: rows.map(r => r.carrier_cost_minor / 100), color: '#888780' },
              { label: 'Stripe fees',    data: rows.map(r => r.stripe_fee_minor   / 100), color: '#6366f1' },
              { label: 'Affiliates',     data: rows.map(r => r.affiliate_minor    / 100), color: '#EF9F27' },
              { label: 'Personnel',      data: rows.map(r => r.personnel_minor    / 100), color: '#D85A30' },
              { label: 'Infrastructure', data: rows.map(r => r.infra_minor        / 100), color: '#0ea5e9' },
              { label: 'Other ops',      data: rows.map(r => r.other_ops_minor    / 100), color: '#f97316' },
              { label: 'Bank fees',      data: rows.map(r => r.bank_fee_minor     / 100), color: '#94a3b8' },
            ]}
            stacked
            yTickFormat={fmtK}
            height={220}
            barThickness={Math.max(4, Math.min(28, Math.floor(600 / Math.max(rows.length, 1))))}
          />}
    </>
  )
}

const ChartRevenueTrend: React.FC = () => {
  const [range, setRange] = useState<TimeRange>(() => getRange('this_year'))
  const [style, setStyle] = useState<ChartStyle>('line')
  const { t } = useTranslation()
  const { data, isLoading, isError } = useChartData<{
    revenueTrend: Array<{ period: string; revenue_minor: number; cost_minor: number; profit_minor: number; ships: number }>
  }>('revenue-trend', range.from, range.to, range.granularity)
  const rows = data?.revenueTrend ?? []
  return (
    <>
      <div className="text-[10px] font-semibold text-ink-500 uppercase tracking-wider mb-1 flex items-center gap-2">
        Revenue / cost / profit trend
        <InfoIcon tip="Revenue (customer billed), label cost (carrier paid), and gross profit. System accounts only." />
        <ChartStylePicker value={style} options={TIME_STYLES} onChange={setStyle} />
      </div>
      <TimeFilter value={range} onChange={setRange} />
      {isLoading  ? <div className="text-[12px] text-ink-400 py-2">{t('common.loading')}</div>
      : isError   ? <div className="text-[12px] text-flame-500 py-2">Failed to load</div>
      : !rows.length ? <div className="text-[12px] text-ink-400 py-2">{t('common.no_data_period')}</div>
      : style === 'line'
        ? <LineChart
            labels={rows.map(r => r.period)}
            height={200}
            yTickFormat={fmtK}
            y2TickFormat={fmtK}
            series={[
              { label: 'Revenue', data: rows.map(r => r.revenue_minor / 100), color: '#1D9E75',  yAxis: 'left' },
              { label: 'Cost',    data: rows.map(r => r.cost_minor    / 100), color: '#888780',  yAxis: 'left', dashed: true },
              { label: 'Profit',  data: rows.map(r => r.profit_minor  / 100), color: '#378ADD',  yAxis: 'right', fill: true },
            ]}
          />
        : <BarChart
            labels={rows.map(r => r.period)}
            series={[
              { label: 'Revenue', data: rows.map(r => r.revenue_minor / 100), color: '#1D9E75' },
              { label: 'Cost',    data: rows.map(r => r.cost_minor    / 100), color: '#888780' },
              { label: 'Profit',  data: rows.map(r => r.profit_minor  / 100), color: '#378ADD' },
            ]}
            yTickFormat={fmtK}
            height={220}
            barThickness={Math.max(4, Math.min(18, Math.floor(560 / Math.max(rows.length, 1))))}
          />}
    </>
  )
}

const ChartMarginByCarrier: React.FC = () => {
  const [range, setRange] = useState<TimeRange>(() => getRange('this_year'))
  const [style, setStyle] = useState<ChartStyle>('bar_h')
  const { t } = useTranslation()
  const { data, isLoading, isError } = useChartData<{
    marginByCarrier: Array<{ carrier: string; revenue_minor: number; cost_minor: number; margin_pct: number; ships: number }>
  }>('margin-by-carrier', range.from, range.to)
  const rows = data?.marginByCarrier ?? []
  const WARN = 17
  const isH   = style === 'bar_h'
  return (
    <>
      <div className="text-[10px] font-semibold text-ink-500 uppercase tracking-wider mb-1 flex items-center gap-2">
        Gross margin % by carrier
        <InfoIcon tip="(Revenue − cost) ÷ revenue per carrier. Red = below 17% — prime negotiation targets." />
        <ChartStylePicker value={style} options={CAT_STYLES} onChange={setStyle} />
      </div>
      <TimeFilter value={range} onChange={setRange} />
      {isLoading  ? <div className="text-[12px] text-ink-400 py-2">{t('common.loading')}</div>
      : isError   ? <div className="text-[12px] text-flame-500 py-2">Failed to load</div>
      : !rows.length ? <div className="text-[12px] text-ink-400 py-2">No carrier data for this period.</div>
      : <BarChart
          labels={rows.map(r => r.carrier)}
          data={rows.map(r => r.margin_pct)}
          colors={rows.map(r => r.margin_pct < WARN ? '#D85A30' : '#1D9E75')}
          height={Math.max(180, rows.length * (isH ? 32 : 48) + 40)}
          horizontal={isH}
          xTickFormat={isH ? v => `${v}%` : undefined}
          yTickFormat={isH ? undefined : v => `${v}%`}
          barThickness={20}
        />}
    </>
  )
}

const ChartAdjExposure: React.FC = () => {
  const [range, setRange] = useState<TimeRange>(() => getRange('this_year'))
  const [style, setStyle] = useState<ChartStyle>('bar_h')
  const { t } = useTranslation()
  const { data, isLoading, isError } = useChartData<{
    adjustmentsByCarrier: Array<{ carrier: string; status: string; total_minor: number; count: number }>
  }>('adj-exposure', range.from, range.to)
  const draftRows  = (data?.adjustmentsByCarrier ?? []).filter(r => r.status === 'DRAFT')
  const sorted     = [...draftRows].sort((a, b) => b.total_minor - a.total_minor)
  const totalDraft = sorted.reduce((s, r) => s + r.total_minor, 0)
  const isH        = style === 'bar_h'
  return (
    <>
      <div className="text-[10px] font-semibold text-ink-500 uppercase tracking-wider mb-1 flex items-center gap-2">
        Pending adj. exposure · DRAFT rebills
        <InfoIcon tip="Carrier adjustment rebills not yet billed to the customer. Go to Reconciliation → Adjustments to invoice." />
        <ChartStylePicker value={style} options={CAT_STYLES} onChange={setStyle} />
      </div>
      <TimeFilter value={range} onChange={setRange} />
      {isLoading  ? <div className="text-[12px] text-ink-400 py-2">{t('common.loading')}</div>
      : isError   ? <div className="text-[12px] text-flame-500 py-2">Failed to load</div>
      : !sorted.length ? <div className="text-[12px] text-ink-400 py-2">No pending adjustments.</div>
      : <>
          <div className="text-[11px] text-flame-600 font-medium mb-1">{fmtK(totalDraft / 100)} total unbilled</div>
          <BarChart
            labels={sorted.map(r => r.carrier)}
            data={sorted.map(r => r.total_minor / 100)}
            color="#D85A30"
            height={Math.max(140, sorted.length * (isH ? 32 : 48) + 40)}
            horizontal={isH}
            xTickFormat={isH ? fmtK : undefined}
            yTickFormat={isH ? undefined : fmtK}
            barThickness={20}
          />
        </>}
    </>
  )
}

const ChartTopCustomers: React.FC = () => {
  const [range, setRange] = useState<TimeRange>(() => getRange('this_year'))
  const [style, setStyle] = useState<ChartStyle>('bar_h')
  const { t } = useTranslation()
  const { data, isLoading, isError } = useChartData<{
    topCustomers: Array<{ customer_name: string; revenue_minor: number; profit_minor: number; ships: number }>
  }>('top-customers', range.from, range.to)
  const rows    = data?.topCustomers ?? []
  const total   = rows.reduce((s, c) => s + c.revenue_minor, 0)
  const top1pct = rows.length > 0 && total > 0 ? Math.round(rows[0].revenue_minor / total * 100) : 0
  const trunc   = (s: string) => s.length > 22 ? s.slice(0, 21) + '…' : s
  const isH     = style === 'bar_h'
  return (
    <>
      <div className="text-[10px] font-semibold text-ink-500 uppercase tracking-wider mb-1 flex items-center gap-2">
        Top customers · revenue
        <InfoIcon tip="Customer concentration risk. If one customer is >50% of revenue, disruption there is an existential event." />
        <ChartStylePicker value={style} options={CAT_STYLES} onChange={setStyle} />
      </div>
      <TimeFilter value={range} onChange={setRange} />
      {isLoading  ? <div className="text-[12px] text-ink-400 py-2">{t('common.loading')}</div>
      : isError   ? <div className="text-[12px] text-flame-500 py-2">Failed to load</div>
      : !rows.length ? <div className="text-[12px] text-ink-400 py-2">No customer data for this period.</div>
      : <>
          {top1pct >= 50 && (
            <div className="text-[11px] text-flame-600 font-medium mb-1">
              {rows[0].customer_name} = {top1pct}% of period revenue
            </div>
          )}
          <BarChart
            labels={rows.map(c => trunc(c.customer_name))}
            data={rows.map(c => c.revenue_minor / 100)}
            color="#378ADD"
            height={Math.max(200, rows.length * (isH ? 32 : 48) + 40)}
            horizontal={isH}
            xTickFormat={isH ? fmtK : undefined}
            yTickFormat={isH ? undefined : fmtK}
            barThickness={20}
          />
        </>}
    </>
  )
}

const ChartInvoiceAging: React.FC = () => {
  const [range, setRange] = useState<TimeRange>(() => getRange('this_year'))
  const [style, setStyle] = useState<ChartStyle>('bar')
  const { t } = useTranslation()
  const { data, isLoading, isError } = useChartData<{
    invoiceAging: Array<{ band: string; total_minor: number; count: number }>
  }>('invoice-aging', range.from, range.to)
  const aging  = data?.invoiceAging ?? []
  const BAND_LABELS: Record<string, string> = { current: '<30 days', '30_60': '30–60 days', over_60: '>60 days' }
  const BAND_COLORS: Record<string, string> = { current: '#378ADD', '30_60': '#EF9F27', over_60: '#D85A30' }
  const bands  = ['current', '30_60', 'over_60']
  const byBand = Object.fromEntries(aging.map(r => [r.band, r]))
  const overdue = byBand['over_60']?.total_minor ?? 0
  const isH     = style === 'bar_h'
  return (
    <>
      <div className="text-[10px] font-semibold text-ink-500 uppercase tracking-wider mb-1 flex items-center gap-2">
        Invoice aging
        <InfoIcon tip="Outstanding customer invoices by age since creation. >60 days = overdue and needs follow-up." />
        <ChartStylePicker value={style} options={CAT_STYLES} onChange={setStyle} />
      </div>
      <TimeFilter value={range} onChange={setRange} />
      {isLoading  ? <div className="text-[12px] text-ink-400 py-2">{t('common.loading')}</div>
      : isError   ? <div className="text-[12px] text-flame-500 py-2">Failed to load</div>
      : <>
          {overdue > 0 && (
            <div className="text-[11px] text-flame-600 font-medium mb-1">
              {fmtK(overdue / 100)} overdue (&gt;60 days)
            </div>
          )}
          <BarChart
            labels={bands.map(b => BAND_LABELS[b] ?? b)}
            data={bands.map(b => (byBand[b]?.total_minor ?? 0) / 100)}
            colors={bands.map(b => BAND_COLORS[b])}
            height={190}
            horizontal={isH}
            yTickFormat={isH ? undefined : fmtK}
            xTickFormat={isH ? fmtK : undefined}
            barThickness={isH ? 20 : 52}
          />
        </>}
    </>
  )
}

const KpiMysysLeakage: React.FC = () => {
  const { t } = useTranslation()
  const { currency } = useCurrency()
  const [range, setRange] = useState<TimeRange>(() => getRange('this_year'))
  const { data, isLoading, isError } = useChartData<{
    mysysLeakage: {
      ytd_minor: number; count: number
      matched_minor: number; matched_count: number
      unverifiable_minor: number; unverifiable_count: number
    }
  }>('mysys-leakage', range.from, range.to)
  const m = data?.mysysLeakage
  return (
    <WidgetShell title="Unattrib. USPS cost" tip={t('my_dashboard.mysys_tile_tip')}>
      <TimeFilter value={range} onChange={setRange} />
      <div className="text-[26px] font-bold text-flame-600">
        {isLoading ? '…' : isError ? '—' : data?.mysysLeakage
          ? fmtMoney(data.mysysLeakage.ytd_minor, currency, { compact: true })
          : '—'}
      </div>
      <div className="text-[11px] mt-1 text-ink-500">
        {m ? t('my_dashboard.mysys_tile_unexplained', { n: FMT_INT.format(m.count) }) : '—'}
      </div>
      {m && (
        <div className="text-[11px] mt-0.5 text-ink-400 num">
          {t('my_dashboard.mysys_tile_matched', { amount: fmtMoney(m.matched_minor, currency, { compact: true }), n: FMT_INT.format(m.matched_count) })}
          {' · '}
          {t('my_dashboard.mysys_tile_unverifiable', { amount: fmtMoney(m.unverifiable_minor, currency, { compact: true }), n: FMT_INT.format(m.unverifiable_count) })}
        </div>
      )}
    </WidgetShell>
  )
}

// ===== Widget shell (common chrome) =======================================
function WidgetShell({ title, tip, children, link }: { title: string; tip: string; children: React.ReactNode; link?: string }) {
  const inner = (
    <>
      <div className="text-[10px] font-semibold text-ink-500 uppercase tracking-wider mb-1.5 flex items-center justify-between">
        <span>{title} <InfoIcon tip={tip} /></span>
        {link && <Icon name="arrow-right" className="w-3 h-3 text-ink-400" />}
      </div>
      {children}
    </>
  )
  return link
    ? <Link to={link} className="block hover:opacity-95">{inner}</Link>
    : <>{inner}</>
}

// =========================================================================
// REGISTRY
// =========================================================================
const REGISTRY: Record<string, WidgetMeta> = {
  'kpi.operating_cash':  { type: 'kpi.operating_cash',  title: 'Operating cash · MTD',  group: 'KPI',  description: 'Real cash margin this month',     defaultSpan: 3, Component: KpiOperatingCash },
  'kpi.active_labels':   { type: 'kpi.active_labels',   title: 'Active labels · today', group: 'KPI',  description: 'Labels purchased today',          defaultSpan: 3, Component: KpiActiveLabels },
  'kpi.mthly_profit':    { type: 'kpi.mthly_profit',    title: 'Profit · MTD',          group: 'KPI',  description: 'Accrual profit this month',       defaultSpan: 3, Component: KpiMthlyProfit },
  'kpi.deposit_float':   { type: 'kpi.deposit_float',   title: 'Customer deposit float',group: 'KPI',  description: 'Customer money you hold',         defaultSpan: 3, Component: KpiDepositFloat },
  'kpi.arpu':            { type: 'kpi.arpu',            title: 'ARPU · 30d',            group: 'KPI',  description: 'Avg revenue per customer',        defaultSpan: 3, Component: KpiArpu },
  'kpi.ltv':             { type: 'kpi.ltv',             title: 'Total LTV',             group: 'KPI',  description: 'Lifetime profit total',           defaultSpan: 3, Component: KpiLtv },
  'kpi.carrier_spend':   { type: 'kpi.carrier_spend',   title: 'Carrier spend · 30d',   group: 'KPI',  description: 'Money paid to carriers · 30d',    defaultSpan: 3, Component: KpiCarrierSpend },
  'kpi.latest_balance':  { type: 'kpi.latest_balance',  title: 'Chase balance',         group: 'KPI',  description: 'Latest Chase posted balance',     defaultSpan: 3, Component: KpiLatestBalance },
  'list.at_risk':        { type: 'list.at_risk',        title: 'At-risk customers',     group: 'List',  description: 'Customers gone quiet',            defaultSpan: 4,  Component: ListAtRisk },
  'list.top_customers':  { type: 'list.top_customers',  title: 'Top customers',         group: 'List',  description: 'By profit this month',            defaultSpan: 4,  Component: ListTopCustomers },
  'list.affiliates':     { type: 'list.affiliates',     title: 'Affiliates pending',    group: 'List',  description: 'Open payouts ready to wire',      defaultSpan: 4,  Component: ListAffiliates },
  'list.disputes':       { type: 'list.disputes',       title: 'Open disputes',         group: 'List',  description: 'Awaiting submit or response',     defaultSpan: 4,  Component: ListDisputesOpen },
  // ── CFO charts ──────────────────────────────────────────────────────────
  'chart.real_pnl':          { type: 'chart.real_pnl',          title: 'Real P&L — all costs',        group: 'Chart', description: 'Revenue minus every cost layer',  defaultSpan: 12, Component: ChartRealPnl },
  'chart.label_volume':             { type: 'chart.label_volume',             title: 'Label volume',                group: 'Chart', description: 'Shipment count over time',              defaultSpan: 12, Component: ChartLabelVolume },
  'chart.label_volume_by_customer': { type: 'chart.label_volume_by_customer', title: 'Label volume by customer',    group: 'Chart', description: 'Stacked shipment counts per customer',  defaultSpan: 12, Component: ChartLabelVolumeByCustomer },
  'chart.revenue_trend':     { type: 'chart.revenue_trend',     title: 'Revenue / cost / profit',    group: 'Chart', description: 'Revenue, cost & profit trend',   defaultSpan: 12, Component: ChartRevenueTrend },
  'chart.margin_by_carrier': { type: 'chart.margin_by_carrier', title: 'Margin % by carrier',         group: 'Chart', description: 'Gross margin per carrier',       defaultSpan: 6,  Component: ChartMarginByCarrier },
  'chart.adj_exposure':      { type: 'chart.adj_exposure',      title: 'Pending adj. exposure',       group: 'Chart', description: 'DRAFT rebills not yet billed',   defaultSpan: 6,  Component: ChartAdjExposure },
  'chart.top_customers':     { type: 'chart.top_customers',     title: 'Top customers · revenue',     group: 'Chart', description: 'Customer concentration risk',    defaultSpan: 6,  Component: ChartTopCustomers },
  'chart.invoice_aging':     { type: 'chart.invoice_aging',     title: 'Invoice aging',               group: 'Chart', description: 'Outstanding invoices by age',    defaultSpan: 6,  Component: ChartInvoiceAging },
  'kpi.mysys_leakage':       { type: 'kpi.mysys_leakage',       title: 'Unattrib. USPS cost',         group: 'KPI',   description: 'Orphan USPS charges · filterable', defaultSpan: 4,  Component: KpiMysysLeakage },
}

// Default layout — what a brand-new user sees (or after clicking Reset).
// CFO charts at the top, then operational KPIs and lists below.
const DEFAULT_LAYOUT: WidgetSpec[] = [
  // ── KPI row ───────────────────────────────────────────────────────────
  { id: 'w1',  type: 'kpi.operating_cash',      span: 3 },
  { id: 'w2',  type: 'kpi.active_labels',        span: 3 },
  { id: 'w3',  type: 'kpi.mthly_profit',         span: 3 },
  { id: 'w11', type: 'kpi.mysys_leakage',        span: 3 },
  // ── Full-width charts ─────────────────────────────────────────────────
  { id: 'chart_pnl', type: 'chart.real_pnl',       span: 12 },
  { id: 'chart0',    type: 'chart.label_volume',             span: 12 },
  { id: 'chart0b',   type: 'chart.label_volume_by_customer', span: 12 },
  { id: 'chart1',    type: 'chart.revenue_trend',            span: 12 },
  // ── Side-by-side charts ───────────────────────────────────────────────
  { id: 'chart2', type: 'chart.margin_by_carrier', span: 6 },
  { id: 'chart3', type: 'chart.adj_exposure',      span: 6 },
  { id: 'chart4', type: 'chart.top_customers',     span: 6 },
  { id: 'chart5', type: 'chart.invoice_aging',     span: 6 },
  // ── Operational lists ─────────────────────────────────────────────────
  { id: 'w5',  type: 'list.at_risk',              span: 4 },
  { id: 'w6',  type: 'list.top_customers',        span: 4 },
  { id: 'w7',  type: 'list.affiliates',           span: 4 },
  { id: 'w8',  type: 'kpi.carrier_spend',         span: 4 },
  { id: 'w9',  type: 'kpi.latest_balance',        span: 4 },
  { id: 'w10', type: 'list.disputes',             span: 4 },
]

const STORAGE_KEY = 'byelabel.finops.dashboard'

// =========================================================================
// COMPONENT
// =========================================================================
export function MyDashboardView() {
  const { t } = useTranslation()
  const [layout, setLayout] = useState<WidgetSpec[]>(() => {
    try {
      const saved = localStorage.getItem(STORAGE_KEY)
      if (saved) return JSON.parse(saved) as WidgetSpec[]
    } catch {}
    return DEFAULT_LAYOUT
  })
  const [dragId, setDragId] = useState<string | null>(null)
  const [dirty, setDirty] = useState(false)
  const [editMode, setEditMode] = useState(false)

  useEffect(() => { setDirty(true) }, [layout])

  function save() {
    try { localStorage.setItem(STORAGE_KEY, JSON.stringify(layout)); setDirty(false) }
    catch (e) { console.error('Save failed', e) }
  }
  function reset() {
    if (window.confirm('Reset dashboard to default layout?')) {
      setLayout(DEFAULT_LAYOUT)
      try { localStorage.removeItem(STORAGE_KEY) } catch {}
    }
  }
  function addWidget(type: string) {
    const meta = REGISTRY[type]
    if (!meta) return
    setLayout([...layout, { id: `w${Date.now()}`, type, span: meta.defaultSpan }])
  }
  function removeWidget(id: string) {
    setLayout(layout.filter(w => w.id !== id))
  }
  function resizeWidget(id: string, span: Span) {
    setLayout(layout.map(w => w.id === id ? { ...w, span } : w))
  }
  function onDragStart(id: string) { setDragId(id) }
  function onDragOver(e: React.DragEvent) { e.preventDefault() }
  function onDrop(targetId: string) {
    if (!dragId || dragId === targetId) { setDragId(null); return }
    const newLayout = [...layout]
    const fromIdx = newLayout.findIndex(w => w.id === dragId)
    const toIdx = newLayout.findIndex(w => w.id === targetId)
    if (fromIdx < 0 || toIdx < 0) return
    const [moved] = newLayout.splice(fromIdx, 1)
    newLayout.splice(toIdx, 0, moved)
    setLayout(newLayout)
    setDragId(null)
  }

  return (
    <div className="space-y-5">
      {/* Hero */}
      <Card className="p-7">
        <div className="flex items-center justify-between gap-6">
          <div>
            <div className="text-[12px] font-semibold text-ink-500 uppercase tracking-wider">My Dashboard</div>
            <div className="mt-2 text-[28px] font-bold text-ink-900 leading-none">
              {layout.length} widget{layout.length === 1 ? '' : 's'} · your composition
            </div>
            <div className="mt-2 text-[13px] text-ink-500">
              {editMode ? 'Drag widgets by their handle to reorder. Click × to remove. Resize from the corner buttons.' : 'Click Edit to rearrange.'}
            </div>
            <LearnTip>Build your own daily glance. Pin the metrics you actually care about; ignore the rest. Saved per-browser (localStorage).</LearnTip>
          </div>
          <div className="flex gap-2">
            <button
              onClick={() => setEditMode(v => !v)}
              className={`px-3 py-1.5 rounded-lg text-sm font-medium border ${editMode ? 'bg-royal-500 text-white border-royal-500' : 'bg-white text-ink-700 border-ink-200 hover:bg-ink-50'}`}
            >
              {editMode ? 'Done' : 'Edit'}
            </button>
            <button
              onClick={save}
              disabled={!dirty}
              className="px-3 py-1.5 rounded-lg text-sm font-medium border border-ink-200 bg-white text-ink-700 hover:bg-ink-50 disabled:opacity-40"
            >
              {dirty ? 'Save layout' : 'Saved'}
            </button>
            <button onClick={reset} className="px-3 py-1.5 rounded-lg text-sm font-medium border border-ink-200 bg-white text-ink-700 hover:bg-ink-50">
              Reset
            </button>
          </div>
        </div>
      </Card>

      {/* Live widget grid */}
      <div
        className={`grid grid-cols-12 gap-3 ${editMode ? 'p-3 bg-royal-50/30 border-2 border-dashed border-royal-300 rounded-lg' : ''}`}
      >
        {layout.map(w => {
          const meta = REGISTRY[w.type]
          if (!meta) return null
          const Comp = meta.Component
          return (
            <div
              key={w.id}
              draggable={editMode}
              onDragStart={() => onDragStart(w.id)}
              onDragOver={onDragOver}
              onDrop={() => onDrop(w.id)}
              style={{ gridColumn: `span ${w.span} / span ${w.span}` }}
              className={`relative group ${dragId === w.id ? 'opacity-40' : ''}`}
            >
              <Card className={`p-4 h-full ${editMode ? 'cursor-grab active:cursor-grabbing border-2 border-ink-200 hover:border-royal-400' : ''}`}>
                {editMode && (
                  <>
                    <div className="absolute top-1.5 left-1.5 text-ink-300">
                      <Icon name="grip-vertical" className="w-3.5 h-3.5" />
                    </div>
                    <div className="absolute top-1.5 right-1.5 flex items-center gap-0.5 bg-white rounded-md shadow-sm border border-ink-100 px-0.5">
                      <button
                        onClick={() => resizeWidget(w.id, Math.max(3, w.span - 1) as Span)}
                        className="px-1 py-0.5 text-[10px] text-ink-500 hover:text-ink-800"
                        title="Shrink"
                      >−</button>
                      <span className="text-[10px] text-ink-400 w-3 text-center">{w.span}</span>
                      <button
                        onClick={() => resizeWidget(w.id, Math.min(12, w.span + 1) as Span)}
                        className="px-1 py-0.5 text-[10px] text-ink-500 hover:text-ink-800"
                        title="Grow"
                      >+</button>
                      <button
                        onClick={() => removeWidget(w.id)}
                        className="px-1 py-0.5 text-[12px] text-flame-500 hover:text-flame-700"
                        title="Remove"
                      >×</button>
                    </div>
                  </>
                )}
                <div className={editMode ? 'mt-3' : ''}>
                  <Comp />
                </div>
              </Card>
            </div>
          )
        })}
        {layout.length === 0 && (
          <div className="col-span-12 p-10 text-center text-ink-400 border-2 border-dashed border-ink-200 rounded-lg">
            No widgets. Add some from the catalog below.
          </div>
        )}
      </div>

      {/* Widget catalog */}
      <Card className="p-5">
        <div className="text-[14px] font-semibold text-ink-800 mb-1">
          Widget catalog
          <InfoIcon tip="Click any widget to add it to your dashboard. You can have multiple of the same widget." />
        </div>
        <div className="text-[12px] text-ink-500 mb-3">
          Click any widget to add it to the bottom of your dashboard.
        </div>
        <div className="grid grid-cols-4 gap-2.5">
          {Object.values(REGISTRY).map(meta => (
            <button
              key={meta.type}
              onClick={() => addWidget(meta.type)}
              className="text-left px-3 py-2.5 border border-ink-200 rounded-lg hover:border-royal-400 hover:bg-ink-50"
            >
              <div className="text-[10px] uppercase tracking-wider text-ink-500 mb-0.5">{meta.group}</div>
              <div className="text-[13px] font-semibold text-royal-700">{meta.title}</div>
              <div className="text-[11px] text-ink-500">{meta.description}</div>
            </button>
          ))}
        </div>
      </Card>
    </div>
  )
}
